import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowUpRight,
  ArrowDownRight,
} from "lucide-react";
import { AppIcon } from "components/AppIcon";

interface RevenueMetric {
  current?: number | null;
  previous?: number | null;
  change_pct?: number | null;
}

interface RevenueOverviewProps {
  financeData?: {
    sales_revenue?: RevenueMetric;
    installation_revenue?: RevenueMetric;
    removal_revenue?: RevenueMetric;
    outstanding_receivables?: RevenueMetric;
    [key: string]: any;
  } | null;
  baseRoute?: string;
}

interface StatItem {
  title: string;
  data?: RevenueMetric;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  fixedIcon: React.ReactNode | null;
  href?: string;
}



const RevenueOverview: React.FC<RevenueOverviewProps> = ({
  financeData,
  baseRoute = "/signage",
}) => {
  const navigate = useNavigate();
  const data = financeData || {};

  const formatCurrency = (val:any) =>
    `$${Number(val || 0).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}`;

  const getChangePercent = (item:any) => {
    const current = Number(item?.current || 0);
    const previous = Number(item?.previous || 0);
    let pct = Number(item?.change_pct || 0);

    if (pct === 0 && previous > 0) {
      pct = ((current - previous) / previous) * 100;
    }
    return Number.isFinite(pct) ? pct : 0;
  };

  const stats = [
    {
      title: "Sales Orders",
      data: data.sales_revenue,
      icon: <AppIcon name="salesOrder" size={32} alt="Sales Orders" />,
      fixedIcon: null,
      href: `${baseRoute}/orders`,
    },
    {
      title: "Installation Revenue",
      data: data.installation_revenue,
      icon: <AppIcon name="profit" size={32} alt="Installation Revenue" />,
      fixedIcon: null,
      href: `${baseRoute}/jobs/installations`,
    },
    {
      title: "Removal Revenue",
      data: data.removal_revenue,
      icon: <AppIcon name="removalRevenue" size={32} alt="Removal Revenue" />,
      fixedIcon: null,
      href: `${baseRoute}/jobs/removalRequests`,
    },
    {
      title: "Outstanding Receivables",
      data: data.outstanding_receivables,
      icon: <AppIcon name="dollar" size={32} alt="Outstanding Receivables" />,
      fixedIcon: (
        <AppIcon name="outstandingBalance" size={18} alt="Warning" />
      ),
    },
    {
      title: "Over Due",
      data: data.overdue_receivables,
      icon: <AppIcon name="outstandingBalance" size={32} alt="Over Due" />,
      fixedIcon: <AppIcon name="outstandingBalance" size={18} alt="Warning" />,
    },
  ];

  return (
    <div className="row g-3 mb-4 revenue-overview-row">
      {stats.map((s, idx) => {
        const changePct = getChangePercent(s.data);
        const isUp = changePct >= 0;

        const topRightIcon = s.fixedIcon
          ? s.fixedIcon
          : isUp
          ? <ArrowUpRight size={16} color="#10b981" />
          : <ArrowDownRight size={16} color="#ef4444" />;

        const cardClassName = `card bg-white shadow-sm${
          s.href ? " text-decoration-none text-reset dashboard-revenue-card-link" : ""
        }`;
        const cardStyle = {
          borderRadius: "14px",
          border: "1px solid #E5E7EB",
          padding: "8px 12px",
          cursor: s.href ? "pointer" : "default",
        };

        const cardBody = (
          <>
              {/* TOP ICON + ARROW */}
              <div className="d-flex justify-content-between align-items-center ">
                {/* LEFT ICON */}
                <div className="d-flex align-items-center justify-content-center">
                  {s.icon}
                </div>

                {/* RIGHT ARROW OR WARNING */}
                <div className="me-1">{topRightIcon}</div>
              </div>

              {/* TITLE */}
              <h6
                className="fw-bold text-gray-600 mt-2 mb-1"
              >
                {s.title}
              </h6>

              {/* VALUE */}
              <h4
                className="text-xl font-medium text-gray-900 mt-1 mb-1"
                style={{ fontSize: "1.4rem" }}
              >
                {formatCurrency(s.data?.current)}
              </h4>

              {/* PERCENTAGE */}
              <span
                style={{
                  color: isUp ? "#10b981" : "#ef4444",
                  fontWeight: 500,
                  fontSize: "0.8rem",
                }}
              >
                {changePct.toFixed(1)}% vs last month
              </span>
          </>
        );

        const handleCardNavigate = () => {
          if (s.href) navigate(s.href);
        };

        return (
          <div key={idx} className="col-12 col-md-6 revenue-overview-card">
            <div
              role={s.href ? "link" : undefined}
              tabIndex={s.href ? 0 : undefined}
              onClick={s.href ? handleCardNavigate : undefined}
              onKeyDown={
                s.href
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleCardNavigate();
                      }
                    }
                  : undefined
              }
              className={cardClassName}
              style={cardStyle}
            >
              {cardBody}
            </div>
          </div>
        );
      })}

      <style>{`
        .card:hover {
          box-shadow: 0 3px 12px rgba(0,0,0,0.06) !important;
          transform: translateY(-1px);
          transition: 0.2s ease;
        }

        .dashboard-revenue-card-link:hover {
          box-shadow: 0 3px 12px rgba(0,0,0,0.06) !important;
          transform: translateY(-1px);
          transition: 0.2s ease;
        }

        /* Keep exactly 5 cards on one line (lg+) */
        @media (min-width: 992px) {
          .revenue-overview-row {
            flex-wrap: nowrap;
          }

          .revenue-overview-card {
            flex: 0 0 20% !important;
            max-width: 20% !important;
          }
        }

          .text-gray-600 {
          color: #4a5565;
        }
          .text-xs {
          font-size: 0.75rem;
        }
          .text-gray-900 {
          color: #101828;
        }
          .text-xl {
          font-size: 1.25rem;
          line-height: 1.75rem;
        }
      `}</style>
    </div>
  );
};

export default RevenueOverview;
